import { useEffect, useReducer, useState, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { onLoginAuthReset, onLoginAuthSubmit } from "../../Store/Slices/loginAuthSlice";
import { onTranslationReset, onTranslationSubmit } from "../../Store/Slices/translationSlice";
import { onGetThemeData } from "../../Store/Slices/themeSlice";
import { clientData, resetClientData } from "../../Store/Slices/clientDataSlice";
import { onSaveCredsGet } from "../../Store/Slices/cardDataSlice";
import axiosInstance from "../../Common/Axios/axiosInstance";
import axiosInstanceClient from "../../Common/Axios/axiosInstanceClient";
import API from "../../Common/Endpoint/serviceConstrants";
import { GetImageUrl } from "../../Components/Utility/Utility";
import getTranslationData from "../../TranslationUtility/TranslationUtility";
import { encryptAES } from "../../Common/Axios/encrypt";
import { loadTheme } from "../../Common/themeLoader";
import { authReducer, INITIAL_AUTH_STATE, AUTH_ACTIONS, buildAuthError } from "./authReducer";

// ── Utility function to dynamically load payment gateway scripts ─────────────
const loadPaymentScript = (src, scriptId) => {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.getElementById(scriptId)) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = src;
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

const useAuthFlow = () => {
  const dispatch = useDispatch();
  const translation = getTranslationData();

  const [authState, dispatchAuth] = useReducer(authReducer, INITIAL_AUTH_STATE);
  const [showModal, setShowModal] = useState(false);
  const [ivkeyReady, setIvkeyReady] = useState(() => !!sessionStorage.getItem("decv"));
  const loginRequestedRef = useRef(false);
  const translationRequestedRef = useRef(false);

  const loginAuthData = useSelector((state) => state.loginAuthReducer);
  const translationData = useSelector((state) => state.translationReducer);
  const clientinfo = useSelector((state) => state.clientDataReducer);
  const passwordChecked = useSelector((state) => state.clientConfigDataReducer?.passwordChecked);

  useEffect(() => {
    dispatch(onLoginAuthReset());
    dispatch(onTranslationReset());
  }, [dispatch]);

  // ── 1. Fetch client info by domain on mount ──────────────────────────────

  useEffect(() => {
    let cancelled = false;

    const fetchClientInfo = async () => {
      try {
        const { data = {} } = await axiosInstance.get(API.clientbyid, {
          params: { platformDomainUrl: window.location.origin },
          headers: {
            ...axiosInstance.defaults.headers,
            Userid: process.env.REACT_APP_U_N,
            Password: process.env.REACT_APP_U_PP,
            Encryption: 1,
          },
        });

        if (cancelled) return;

        setIvkeyReady(!!sessionStorage.getItem("decv"));

        if (data?.response?.clientCode) {
          axiosInstance.defaults.headers["client-code"] = data.response.clientCode;
          dispatch(clientData(data.response));
          const clientTitle = data.response.title.trim().toLowerCase();
          loadTheme(clientTitle);
          // ── Load payment gateway script based on payment partner ────────────
          const paymentPartner = data?.response?.paymentPartner;
          if (paymentPartner) {
            try {
              if (paymentPartner.toLowerCase().includes("razorpay")) {
                await loadPaymentScript(
                  "https://checkout.razorpay.com/v1/checkout.js",
                  "razorpay-script"
                );
              } else if (paymentPartner.toLowerCase().includes("payu")) {
                const payuScriptUrl = process.env.REACT_APP_PAYU_SCRIPT_URL;
                if (payuScriptUrl) {
                  await loadPaymentScript(payuScriptUrl, "payu-script");
                }
              }
            } catch (scriptError) {
              console.error("Payment script loading error:", scriptError);
              // Continue with auth flow even if script loading fails
            }
          }
        } else {
          dispatch(resetClientData());
          dispatchAuth({
            type: AUTH_ACTIONS.SET_ERROR,
            payload: buildAuthError("401", translation?.errorName, translation?.applicationUrlIsNotRegistered, translation),
          });
        }
      } catch {
        if (!cancelled) {
          dispatchAuth({
            type: AUTH_ACTIONS.SET_ERROR,
            payload: buildAuthError("401", translation?.errorName, translation?.applicationUrlIsNotRegistered, translation),
          });
        }
      }
    };

    fetchClientInfo();
    return () => { cancelled = true; };
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ translation excluded intentionally: used only in error paths at mount time;
  //   adding it would cause refetches on every re-render before translations load.

  // ── 2. Trigger login auth once client code and encryption key are ready ───

  useEffect(() => {
    const decvValue = sessionStorage.getItem("decv");
    // const decvValue = decryptAES(sessionStorage.getItem("decv"), 1);
    if (!clientinfo.clientCode || !decvValue) return;

    const sharedHeaders = {
      "client-code": clientinfo.clientCode,
      "partner-code": clientinfo.userType,
      Encryption: 2,
    };
    Object.assign(axiosInstance.defaults.headers, sharedHeaders);
    Object.assign(axiosInstanceClient.defaults.headers, sharedHeaders);

    dispatch(onTranslationReset());
    loginRequestedRef.current = true;
    dispatch(
      onLoginAuthSubmit({
        partnerCode: clientinfo.userType,
        accessKey: process.env.REACT_APP_E_K,
        secretKey: process.env.REACT_APP_PK,
      })
    );
  }, [clientinfo.clientCode, clientinfo.userType, ivkeyReady, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Handle login auth response ────────────────────────────────────────

  useEffect(() => {
    const { status_code, data } = loginAuthData ?? {};
    if (!loginRequestedRef.current) return;
    if (!status_code) return;

    if (status_code === 200) {
      const authEntry = data?.[0];

      document.title = clientinfo.title;
      const faviconLink = document.querySelector("link[rel='icon']");
      if (faviconLink) faviconLink.href = GetImageUrl(clientinfo?.favIconImage);

      sessionStorage.setItem("clientCode", authEntry?.clientId);
      sessionStorage.setItem("expirytime", String(new Date(authEntry?.expiryTime)));
      sessionStorage.setItem("expiryTimeInMintue", authEntry?.expiryTimeInMintue);
      sessionStorage.setItem("authToken", encryptAES(authEntry?.token));

      dispatch(onGetThemeData({ id: parseInt(clientinfo.themeId, 10) }));
      translationRequestedRef.current = true;
      dispatch(onTranslationSubmit());
      dispatch(onSaveCredsGet("V3IntegrationFront"));
      dispatch(onLoginAuthReset());
    } else {
      dispatchAuth({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: buildAuthError(status_code, translation?.internalServerError, translation?.permissionToViewThisResource, translation),
      });
    }
  }, [loginAuthData, dispatch, clientinfo, translation]);

  // ── 4. Handle translation response ───────────────────────────────────────

  useEffect(() => {
    const { status_code, isLoading } = translationData ?? {};
    if (!translationRequestedRef.current) return;
    if (!status_code) return;

    if (status_code === 200 && !isLoading) {
      dispatchAuth({ type: AUTH_ACTIONS.SET_AUTHENTICATED });
      dispatch(onTranslationReset());
    } else if (status_code !== 200) {
      dispatchAuth({
        type: AUTH_ACTIONS.SET_ERROR,
        payload: buildAuthError("500", translation?.internalServerError, translation?.permissionToViewThisResource, translation),
      });
    }
  }, [translationData, dispatch, translation]);

  // ── 5. Password protection modal ─────────────────────────────────────────

  const passwordConfig = useMemo(() => {
    const config = clientinfo?.clientData?.clientConfigurations?.find(
      (item) => item?.configKey?.toLowerCase() === "ispasswordprotected"
    );
    if (!config) return { isProtected: false, description: "" };

    const { configValue: val, description = "" } = config;
    const isProtected = val === true || val === "true" || val === 1 || val === "1";
    return { isProtected, description };
  }, [clientinfo?.clientData?.clientConfigurations]);

  useEffect(() => {
    setShowModal(passwordConfig.isProtected && !passwordChecked);
  }, [passwordConfig.isProtected, passwordChecked]);

  const handleCloseModal = useCallback(() => setShowModal(false), []);

  return { authState, showModal, passwordConfig, handleCloseModal };
};

export default useAuthFlow;
